import * as Yup from 'yup'

import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons'
import { Box, Button, Card, CardContent, Divider, Grid, IconButton, TextField, Typography } from '@material-ui/core'
import { FlexHorizontal, Spacer } from '@vestaboard/installables'

import { Checkbox } from '@mui/material'
import React from 'react'
import { useFormik } from 'formik'

export interface IBannerFormData {
  title: string
  description: string
  backgroundColor: string
  positionInFeed: number
  enabled?: boolean
  buttons: Array<{
    title: string
    deepLinkUrl: string
  }>
}

interface IBannerForm {
  banner?: IBannerFormData
  onSave: (banner: IBannerFormData) => void
  onCancel: () => void
  onDelete?: () => void
  loading?: boolean
}

const BannerFormSchema = Yup.object().shape({
  title: Yup.string().optional(),
  enabled: Yup.bool().optional(),
  description: Yup.string().optional(),
  backgroundColor: Yup.string().optional(),
  positionInFeed: Yup.number().min(0, 'Position must be 0 or greater').required('Position is required'),
  buttons: Yup.array().of(
    Yup.object().shape({
      title: Yup.string().required('Button title is required'),
      deepLinkUrl: Yup.string().required('Deep link URL is required')
    })
  )
})

export const BannerForm = (props: IBannerForm) => {
  const formik = useFormik({
    validationSchema: BannerFormSchema,
    initialValues: {
      title: props.banner?.title || '',
      description: props.banner?.description || '',
      backgroundColor: props.banner?.backgroundColor || '',
      positionInFeed: props.banner?.positionInFeed || 0,
      buttons: props.banner?.buttons || [],
      enabled: props.banner?.enabled || false
    },
    onSubmit: values => {
      props.onSave(values)
    }
  })

  const addButton = () => {
    formik.setFieldValue('buttons', [...formik.values.buttons, { title: '', deepLinkUrl: '' }])
  }

  const removeButton = (index: number) => {
    const newButtons = [...formik.values.buttons]
    newButtons.splice(index, 1)
    formik.setFieldValue('buttons', newButtons)
  }

  const updateButton = (index: number, field: string, value: string) => {
    const newButtons = [...formik.values.buttons]
    newButtons[index] = { ...newButtons[index], [field]: value }
    formik.setFieldValue('buttons', newButtons)
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardContent style={{ padding: 24 }}>
          <Typography variant='h5' gutterBottom>
            {props.banner ? 'Edit Banner' : 'Create New Banner'}
          </Typography>
          <Spacer size='large' />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant='outlined'
                label='Title'
                name='title'
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant='outlined'
                label='Position in Feed'
                name='positionInFeed'
                type='number'
                value={formik.values.positionInFeed}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.positionInFeed && Boolean(formik.errors.positionInFeed)}
                helperText={formik.touched.positionInFeed && formik.errors.positionInFeed}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                variant='outlined'
                label='Description'
                name='description'
                multiline
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant='outlined'
                label='Background Color'
                name='backgroundColor'
                value={formik.values.backgroundColor}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.backgroundColor && Boolean(formik.errors.backgroundColor)}
                helperText={formik.touched.backgroundColor && formik.errors.backgroundColor}
                InputProps={{
                  startAdornment: (
                    <Box
                      style={{
                        width: 24,
                        height: 24,
                        backgroundColor: formik.values.backgroundColor,
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        marginRight: 8
                      }}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box display='flex' alignItems='center' height='100%'>
                <Checkbox checked={formik.values.enabled} onChange={formik.handleChange} name='enabled' />{' '}
                <Typography>Enable in Feed</Typography>
              </Box>
            </Grid>
          </Grid>

          <Spacer size='extraLarge' />
          <Divider />
          <Spacer size='large' />

          <FlexHorizontal spaceBetween>
            <Typography variant='h6'>Buttons</Typography>
            <Button variant='outlined' color='primary' startIcon={<AddIcon />} onClick={addButton}>
              Add Button
            </Button>
          </FlexHorizontal>

          <Spacer size='medium' />

          {formik.values.buttons.map((button, index) => (
            <Box key={index} style={{ marginBottom: 16 }}>
              <Card variant='outlined' style={{ padding: 16 }}>
                <FlexHorizontal spaceBetween>
                  <Box style={{ flex: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant='outlined'
                          label='Button Title'
                          value={button.title}
                          onChange={e => updateButton(index, 'title', e.target.value)}
                          error={
                            formik.touched.buttons?.[index]?.title &&
                            Boolean((formik.errors.buttons as any)?.[index]?.title)
                          }
                          helperText={
                            formik.touched.buttons?.[index]?.title && (formik.errors.buttons as any)?.[index]?.title
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          variant='outlined'
                          label='Deep Link URL'
                          value={button.deepLinkUrl}
                          onChange={e => updateButton(index, 'deepLinkUrl', e.target.value)}
                          error={
                            formik.touched.buttons?.[index]?.deepLinkUrl &&
                            Boolean((formik.errors.buttons as any)?.[index]?.deepLinkUrl)
                          }
                          helperText={
                            formik.touched.buttons?.[index]?.deepLinkUrl &&
                            (formik.errors.buttons as any)?.[index]?.deepLinkUrl
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <IconButton onClick={() => removeButton(index)} color='secondary' style={{ marginLeft: 8 }}>
                    <DeleteIcon />
                  </IconButton>
                </FlexHorizontal>
              </Card>
            </Box>
          ))}

          <Spacer size='extraLarge' />
          <Divider />
          <Spacer size='large' />

          <FlexHorizontal>
            <Button variant='outlined' color='default' size='large' onClick={props.onCancel} disabled={props.loading}>
              Cancel
            </Button>
            <Box width={16} />
            <Button variant='contained' color='primary' size='large' type='submit' disabled={props.loading}>
              {props.loading ? 'Saving...' : 'Save'}
            </Button>
            <Box width={16} />
            {props.onDelete && (
              <Button
                variant='contained'
                color='secondary'
                size='large'
                onClick={props.onDelete}
                disabled={props.loading}>
                Delete
              </Button>
            )}
          </FlexHorizontal>
        </CardContent>
      </Card>
    </form>
  )
}
